import { queryOptions } from "@tanstack/react-query";
import type { CurrencyService } from "@/services/currency.service";
import type { OAuthIdpService } from "@/services/oauth-idp.service";
import type { TimezoneService } from "@/services/timezone.service";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * Lists that belong to no workspace and change only on a backend deploy: the
 * ISO 4217 currencies (`GET /api/currencies/`), the time zones
 * (`GET /api/timezones/`) and the OAuth scope catalog
 * (`GET /api/oauth/scopes/`). Each was fetched again by every picker that
 * mounted it, or kept in a module-level cache of its own (NG-2803).
 *
 * Reference data, read under `GLOBAL_TENANT`: `['*', uid, <list>]`.
 *
 * @writers None: the lists change only with a backend deploy.
 */
export function currenciesQuery(
  service: Pick<CurrencyService, "listCurrencies">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: scopedKey(scope, "currencies"),
    queryFn: scopedRead((request) => service.listCurrencies(request), { retry: "safe-read" }),
  });
}

export function timezonesQuery(service: Pick<TimezoneService, "listTimezones">, scope: QueryScope) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: scopedKey(scope, "timezones"),
    queryFn: scopedRead((request) => service.listTimezones(request), { retry: "safe-read" }),
  });
}

/**
 * The scope catalog, as the picker inside `workspaceSlug` may grant it (NG-3761):
 * the server leaves out the configuration scopes that workspace's flags withhold,
 * so each workspace's answer is its own entry. `null` is the instance pickers'.
 */
export function oauthScopeCatalogQuery(
  service: Pick<OAuthIdpService, "listScopes">,
  scope: QueryScope,
  workspaceSlug: string | null = null,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: workspaceSlug
      ? scopedKey(scope, "oauth-scopes", workspaceSlug)
      : scopedKey(scope, "oauth-scopes"),
    queryFn: scopedRead((request) => service.listScopes(workspaceSlug, request), {
      retry: "safe-read",
    }),
  });
}
